// RIASEC result and program matching, shared by the chat (and testable on its own).
// Scoring is unchanged: 42 Yes/No questions, +1 per Yes to the question's type, 0-7 per type.

import { riasecQuestions } from '../../assessments/riasecQuestions';

export const TYPE_ORDER = ['realistic', 'investigative', 'artistic', 'social', 'enterprising', 'conventional'];

// Everyday names a 16-year-old reads first; the official name stays small beside them
export const TYPES = {
  realistic: { everyday: 'Hands-on', official: 'Realistic', meaning: 'You like building, fixing and working with tools or nature.' },
  investigative: { everyday: 'Thinking', official: 'Investigative', meaning: 'You like figuring out how things work.' },
  artistic: { everyday: 'Creative', official: 'Artistic', meaning: 'You like making things and sharing ideas.' },
  social: { everyday: 'People', official: 'Social', meaning: 'You like helping and working with others.' },
  enterprising: { everyday: 'Leading', official: 'Enterprising', meaning: 'You like leading, persuading and selling.' },
  conventional: { everyday: 'Organizing', official: 'Conventional', meaning: 'You like order, numbers and clear steps.' },
};

export const MAX_PER_TYPE = 7;

/** Six scores from the answers ('yes' / 'no' by question index). */
export const scoreAnswers = (answers = []) => {
  const scores = Object.fromEntries(TYPE_ORDER.map((t) => [t, 0]));
  riasecQuestions.forEach((q, i) => {
    if (answers[i] === 'yes' && q.type in scores) scores[q.type] += 1;
  });
  return scores;
};

/** All six, highest first; equal scores keep the R-I-A-S-E-C order only for listing. */
export const rankTypes = (scores) =>
  [...TYPE_ORDER].sort((a, b) => (scores[b] || 0) - (scores[a] || 0) || TYPE_ORDER.indexOf(a) - TYPE_ORDER.indexOf(b));

export const topThree = (scores) => rankTypes(scores).slice(0, 3);

/** The code the Conversation record stores: [[type, score], …] for the top three (format unchanged). */
export const riasecCodeOf = (scores) => topThree(scores).map((t) => [t, scores[t] || 0]);

/**
 * How clear the result is, in words a student understands:
 * low   - said yes to very few things;  flat - answers spread out evenly;
 * tied  - the top three are within a point; clear - otherwise.
 */
export const clarityOf = (scores) => {
  const ranked = rankTypes(scores).map((t) => scores[t] || 0);
  const highest = ranked[0];
  const lowest = ranked[ranked.length - 1];
  if (highest <= 2) return 'low';
  if (highest - lowest <= 2) return 'flat';
  if (highest - ranked[2] <= 1) return 'tied';
  return 'clear';
};

// ---------- strands ----------

export const STRANDS = {
  ABM: { label: 'ABM', fits: ['enterprising', 'conventional'] },
  STEM: { label: 'STEM', fits: ['investigative', 'realistic'] },
  HUMSS: { label: 'HUMSS', fits: ['social', 'artistic', 'enterprising'] },
  ARTS: { label: 'Arts and Design', fits: ['artistic'] },
  GAS: { label: 'GAS', fits: 'open' },
  TVL_ICT: { label: 'TVL – ICT', fits: ['investigative', 'realistic', 'conventional'] },
  TVL_HE: { label: 'TVL – Home Economics', fits: ['realistic', 'social'] },
  TVL_AFA: { label: 'TVL – Agri-Fishery Arts', fits: ['realistic', 'investigative'] },
  TVL_IA: { label: 'TVL – Industrial Arts', fits: ['realistic', 'conventional'] },
};

/** Profile and course strand text ("TVL - Information and Communications Technology", "STEM" …) → a STRANDS key. */
export const strandKey = (value) => {
  const s = String(value || '').toUpperCase().replace(/[–—]/g, '-').replace(/\s+/g, ' ').trim();
  if (!s || s === 'N/A' || s === 'NA' || s === 'NONE') return null;
  if (s.includes('ARTS') && s.includes('DESIGN')) return 'ARTS';
  if (s.startsWith('TVL') || s.includes('INFORMATION AND COMMUNICATION') || s === 'ICT' || s.includes('HOME ECONOMICS') || s.includes('AGRI') || s.includes('INDUSTRIAL')) {
    if (s.includes('INFORMATION') || s.includes('ICT')) return 'TVL_ICT';
    if (s.includes('HOME')) return 'TVL_HE';
    if (s.includes('AGRI')) return 'TVL_AFA';
    if (s.includes('INDUSTRIAL')) return 'TVL_IA';
    return null;
  }
  if (s.startsWith('ABM')) return 'ABM';
  if (s.startsWith('STEM')) return 'STEM';
  if (s.startsWith('HUMSS')) return 'HUMSS';
  if (s.startsWith('GAS')) return 'GAS';
  return null;
};

export const strandLabel = (key) => (key && STRANDS[key] ? STRANDS[key].label : '');

/**
 * How the student's interests sit with their strand.
 * none: no strand on file · skip: the result is too unclear to judge · open: GAS
 * fits: the strand's interests include the first interest · partly: the second or third · diff: none of the three
 */
export const strandVerdict = ({ top, strand, clarity }) => {
  if (!strand || !STRANDS[strand]) return 'none';
  if (clarity === 'flat' || clarity === 'low') return 'skip';
  const fits = STRANDS[strand].fits;
  if (fits === 'open') return 'open';
  if (fits.includes(top[0])) return 'fits';
  if (fits.includes(top[1]) || fits.includes(top[2])) return 'partly';
  return 'diff';
};

// ---------- programs ----------

const LETTER = { R: 'realistic', I: 'investigative', A: 'artistic', S: 'social', E: 'enterprising', C: 'conventional' };

/** A course's riasec_area entries ("ARTISTIC", "A", "Artistic") → type keys. */
export const courseTypes = (course) =>
  (Array.isArray(course?.riasec_area) ? course.riasec_area : [])
    .map((v) => String(v || '').trim())
    .map((v) => (v.length === 1 ? LETTER[v.toUpperCase()] : v.toLowerCase()))
    .filter((v) => TYPE_ORDER.includes(v));

export const courseStrands = (course) =>
  [...new Set((Array.isArray(course?.strand) ? course.strand : []).map(strandKey).filter(Boolean))];

export const normalizeName = (name) => String(name || '').trim().toLowerCase();

const WEIGHTS = [3, 2, 1];

const TIER_RANK = { great: 3, good: 2, look: 1 };

/** A program's approved interest code as type keys, best first ([] until the approver accepts one). */
export const courseCode = (course) =>
  [...new Set((Array.isArray(course?.riasec_code) ? course.riasec_code : []).map((v) => LETTER[String(v || '').trim().toUpperCase()]).filter(Boolean))].slice(0, 3);

// Share of programs that must have an approved code before matching switches to codes. Before
// that, unreviewed programs (scored by their one old tag) would sink below the reviewed ones.
export const CODES_READY_SHARE = 0.9;

/** True once enough programs have approved codes: matching then compares codes instead of single tags. */
export const codesReady = (courses = []) =>
  courses.length > 0 && courses.filter((course) => courseCode(course).length > 0).length >= CODES_READY_SHARE * courses.length;

// Closeness of two types on Holland's hexagon (R-I-A-S-E-C-R): same 3, next to each other 2,
// one apart 1, opposite 0
const closeness = (a, b) => {
  const d = Math.abs(TYPE_ORDER.indexOf(a) - TYPE_ORDER.indexOf(b));
  return 3 - Math.min(d, 6 - d);
};

// A code shorter than three letters is filled with its first letter (Gore & Brown, 2006)
const fill = (code) => (code.length ? [...code, code[0], code[0]].slice(0, 3) : []);

/**
 * Brown & Gore's (1994) C index between the student's top three and a program's code, 0-18:
 * position by position, closeness on the hexagon times 3, 2 and 1.
 */
export const congruence = (top, code) => {
  const a = fill(top);
  const b = fill(code);
  if (!a.length || !b.length) return 0;
  return a.reduce((sum, t, i) => sum + WEIGHTS[i] * closeness(t, b[i]), 0);
};

// C index cut-offs: 14+ is a great match, 12-13 a good one. With the O*NET codes every possible
// result has at least five programs at 12+ (checked against all 262,144 score patterns).
export const GREAT_FIT = 14;
export const GOOD_FIT = 12;

/** Fit by program code: the code the approver accepted, or the single old tag until then. */
const codeFitOf = (course, top) => {
  const code = courseCode(course).length ? courseCode(course) : courseTypes(course).slice(0, 1);
  const score = congruence(top, code);
  const tier = score >= GREAT_FIT ? 'great' : score >= GOOD_FIT ? 'good' : score > 0 ? 'look' : null;
  return { score, tier, matched: top.filter((t) => code.includes(t)), code };
};

/**
 * Fit of one course with the student's top three. With program codes (`byCode`) this
 * is the C index above. Without them it falls back to the old single-tag rule (3 points for
 * the first interest, 2 for the second, 1 for the third):
 *   Great match  - the first interest, and either another top interest or nothing else
 *   Good match   - the first interest among other areas, the second and third together,
 *                  or a single-area course in the second interest
 *   Worth a look - any other course that touches the top three
 * `score` sorts within and across tiers (tier first, then points).
 */
export const fitOf = (course, top, byCode = courseCode(course).length > 0) => {
  if (byCode) return codeFitOf(course, top);
  const types = courseTypes(course);
  const matched = top.filter((t) => types.includes(t));
  const points = matched.reduce((sum, t) => sum + WEIGHTS[top.indexOf(t)], 0);
  let tier = null;
  if (matched.length) {
    const first = matched.includes(top[0]);
    const focused = types.length === 1;
    if (first && (matched.length > 1 || focused)) tier = 'great';
    else if (first || points >= 3 || (focused && matched[0] === top[1])) tier = 'good';
    else tier = 'look';
  }
  return { score: tier ? TIER_RANK[tier] * 10 + points : 0, tier, matched };
};

export const TIER_LABEL = { great: 'Great match', good: 'Good match', look: 'Worth a look' };

/** "Fits your People and Thinking interests" */
export const reasonOf = (matched) => {
  const names = matched.map((t) => TYPES[t].everyday);
  if (!names.length) return 'Close to your interests';
  const list = names.length === 1 ? names[0] : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
  return `Fits your ${list} interest${names.length > 1 ? 's' : ''}`;
};

/** How many programs the chat shows first. */
export const TOP_COUNT = 5;

/**
 * The programs that fit the top three, best first: by fit, then offered at the student's school,
 * then in their strand, then name. With program codes only great and good matches are kept,
 * topped up with the next closest so there are always TOP_COUNT to show; with the old single
 * tags every course that touches the top three is kept.
 */
export const rankPrograms = ({ courses = [], top, strand = null, offeredNames = new Set() }) => {
  const byCode = codesReady(courses);
  const rows = courses
    .map((course) => {
      const fit = fitOf(course, top, byCode);
      const strands = courseStrands(course);
      return {
        course,
        ...fit,
        strands,
        inStrand: Boolean(strand && strands.includes(strand)),
        atSchool: offeredNames.has(normalizeName(course.name)),
      };
    })
    .filter((row) => row.tier)
    .sort(
      (a, b) =>
        b.score - a.score ||
        Number(b.atSchool) - Number(a.atSchool) ||
        Number(b.inStrand) - Number(a.inStrand) ||
        String(a.course.name).localeCompare(String(b.course.name)),
    );
  if (!byCode) return rows;
  const fits = rows.filter((row) => row.tier !== 'look');
  return fits.length >= TOP_COUNT ? fits : rows.slice(0, TOP_COUNT);
};

/** The courses a student in this strand usually takes, ranked by fit with their interests (no fit last). */
export const rankStrandPrograms = ({ courses = [], top, strand, offeredNames = new Set() }) =>
  courses
    .filter((course) => strand && courseStrands(course).includes(strand))
    .map((course) => ({
      course,
      ...fitOf(course, top, codesReady(courses)),
      strands: courseStrands(course),
      inStrand: true,
      atSchool: offeredNames.has(normalizeName(course.name)),
    }))
    .sort((a, b) => b.score - a.score || Number(b.atSchool) - Number(a.atSchool) || String(a.course.name).localeCompare(String(b.course.name)));

/** The per-type lists the Conversation record stores (same shape as before: { social: [courses] … }). */
export const recommendationByType = (courses, top) =>
  Object.fromEntries(top.map((t) => [t, courses.filter((c) => courseTypes(c).includes(t))]));

/** Best name match for "Search for a program": exact, then starts-with, then contains (case-insensitive). */
export const findCourse = (courses, query) => {
  const q = normalizeName(query).replace(/^(bs|ba|bachelor of( science in)?)\s+/, '');
  if (q.length < 2) return [];
  const scored = courses
    .map((course) => {
      const name = normalizeName(course.name);
      const bare = name.replace(/^(bs|ba|bachelor of( science in| arts in)?)\s+/, '');
      const rank = name === q || bare === q ? 0 : bare.startsWith(q) || name.startsWith(q) ? 1 : name.includes(q) ? 2 : 9;
      return { course, rank };
    })
    .filter((row) => row.rank < 9)
    .sort((a, b) => a.rank - b.rank || String(a.course.name).localeCompare(String(b.course.name)));
  return scored.slice(0, 3).map((row) => row.course);
};

// ---------- search ----------

// Words students type that the catalog spells differently: jobs and short forms
const ALIASES = {
  nurse: ['nursing'], nurses: ['nursing'],
  doctor: ['medicine', 'medical'], doctors: ['medicine', 'medical'], med: ['medicine', 'medical'],
  engineer: ['engineering'], engineers: ['engineering'],
  teacher: ['education'], teachers: ['education'], teaching: ['education'], teach: ['education'],
  it: ['information technology'], computer: ['computer', 'information technology'], computers: ['computer', 'information technology'],
  programmer: ['computer science', 'information technology'], programming: ['computer science', 'information technology'], coding: ['computer science', 'information technology'],
  psych: ['psychology'], psychologist: ['psychology'],
  accountant: ['accountancy', 'accounting'], accounting: ['accountancy'],
  architect: ['architecture'], architects: ['architecture'],
  pilot: ['aeronautical', 'aviation', 'aircraft'], pilots: ['aeronautical', 'aviation', 'aircraft'],
  chef: ['culinary', 'hospitality'], cook: ['culinary'], cooking: ['culinary'], baking: ['culinary'],
  police: ['criminology'], cop: ['criminology'], crime: ['criminology'], detective: ['criminology'],
  lawyer: ['law', 'legal', 'political science'], attorney: ['law', 'legal'],
  business: ['business', 'management', 'entrepreneurship'], entrepreneur: ['entrepreneurship', 'business'],
  artist: ['fine arts', 'arts'], art: ['arts', 'fine arts'], drawing: ['fine arts', 'multimedia'], design: ['design'], designer: ['design'],
  music: ['music'], musician: ['music'], singer: ['music'],
  dentist: ['dentistry'], pharmacist: ['pharmacy'], vet: ['veterinary'], veterinarian: ['veterinary'],
  physio: ['physical therapy'], therapist: ['therapy'],
  seaman: ['marine', 'maritime'], sailor: ['marine', 'maritime'], seafarer: ['marine', 'maritime'],
  hotel: ['hospitality', 'hotel'], tourism: ['tourism'], tour: ['tourism'],
  writer: ['communication', 'journalism', 'literature'], journalist: ['journalism', 'communication'],
  math: ['mathematics'], maths: ['mathematics'],
  social: ['social work', 'sociology'],
  farmer: ['agriculture', 'agricultural'], farming: ['agriculture', 'agricultural'],
  animator: ['animation', 'multimedia'], games: ['game', 'entertainment', 'multimedia'], gaming: ['game', 'entertainment', 'multimedia'],
};
const STOP = new Set(['bs', 'bsc', 'ab', 'ba', 'bachelor', 'bachelors', 'of', 'in', 'and', 'the', 'degree', 'program', 'course', 'major']);
const clean = (text) => String(text || '').toLowerCase().replace(/[^a-z0-9\s]/g, ' ').replace(/\s+/g, ' ').trim();
const words = (text) => clean(text).split(' ').filter((w) => w && !STOP.has(w));
const trigrams = (word) => {
  const padded = `  ${word} `;
  const out = new Set();
  for (let i = 0; i < padded.length - 2; i += 1) out.add(padded.slice(i, i + 3));
  return out;
};
const similarity = (a, b) => {
  const ta = trigrams(a);
  const tb = trigrams(b);
  let shared = 0;
  ta.forEach((t) => {
    if (tb.has(t)) shared += 1;
  });
  return (2 * shared) / (ta.size + tb.size);
};
const commonPrefix = (a, b) => {
  let n = 0;
  while (n < a.length && n < b.length && a[n] === b[n]) n += 1;
  return n;
};
/** How well one typed word fits one word of a program name. */
const wordScore = (token, word) => {
  if (word === token) return 3;
  if (word.startsWith(token)) return token.length >= 3 ? 2 : 0.6;
  if (token.startsWith(word) && word.length >= 4) return 1.5;
  if (commonPrefix(token, word) >= 4) return 1.3; // nurse / nursing
  if (token.length >= 4 && word.length >= 4 && similarity(token, word) >= 0.5) return 1; // a typo
  return 0;
};

/**
 * Programs for what a student typed: a name, part of one, a job ("nurse", "pilot") or a near
 * miss ("enginering"), best first. Empty when nothing comes close.
 */
export const searchCourses = (courses, query, limit = 8) => {
  const q = clean(query);
  if (q.length < 2) return [];
  const tokens = words(q);
  if (!tokens.length) return [];
  const aliasTokens = [...new Set(tokens.flatMap((t) => (ALIASES[t] || []).flatMap(words)))].filter((t) => !tokens.includes(t));
  return courses
    .map((course) => {
      const name = clean(course.name);
      const nameWords = words(name);
      if (!nameWords.length) return { course, score: 0 };
      let score = 0;
      tokens.forEach((t) => {
        score += Math.max(0, ...nameWords.map((w) => wordScore(t, w)));
      });
      aliasTokens.forEach((t) => {
        score += 0.9 * Math.max(0, ...nameWords.map((w) => wordScore(t, w)));
      });
      if (name === q || nameWords.join(' ') === tokens.join(' ')) score += 5;
      else if (name.includes(q)) score += 2;
      return { course, score };
    })
    .filter((row) => row.score >= 1)
    .sort((a, b) => b.score - a.score || String(a.course.name).localeCompare(String(b.course.name)))
    .slice(0, limit)
    .map((row) => row.course);
};
